import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PaymentMethodsSettingsPage() {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/payment-methods`, { headers: headers() });
      setMethods(res.data.methods || []);
    } catch (e) {
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addMethod = async () => {
    if (!newName.trim()) { toast.error('Enter a method name'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/payment-methods`, { name: newName.trim() }, { headers: headers() });
      toast.success('Payment method added');
      setNewName('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add method');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      await axios.put(`${API_BASE_URL}/payment-methods/${m.id}`, { active: !(m.active !== false) }, { headers: headers() });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete payment method "${m.name}"? Existing orders keep their recorded method.`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/payment-methods/${m.id}`, { headers: headers() });
      toast.success('Payment method deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6" data-testid="payment-methods-settings-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="text-wt-text-muted" data-testid="back-to-settings-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
          <CreditCard className="h-6 w-6 text-purple-400" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-wt-text-primary">Payment Methods</h1>
          <p className="text-sm text-wt-text-secondary">Methods staff can pick when creating an order with payment already received (e.g. Bank Transfer, Cash, Card on POS)</p>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-wt-paper border border-wt-border rounded-xl p-6 space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="New payment method name (e.g. Bank Transfer)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addMethod()}
            className="bg-wt-bg border-wt-border text-wt-text-primary"
            data-testid="new-payment-method-input"
          />
          <Button onClick={addMethod} disabled={saving} className="bg-wt-gold hover:bg-wt-gold-hover text-black shrink-0" data-testid="add-payment-method-btn">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>

        {loading ? (
          <p className="text-wt-text-muted text-center py-6">Loading…</p>
        ) : methods.length === 0 ? (
          <p className="text-wt-text-muted text-center py-6" data-testid="no-payment-methods-message">No payment methods yet — add your first one above</p>
        ) : (
          <div className="space-y-2">
            {methods.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 p-3 bg-wt-bg rounded-lg border border-wt-border" data-testid={`payment-method-row-${m.name}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-wt-text-primary truncate">{m.name}</span>
                  {m.active === false && <Badge className="bg-gray-500/20 text-gray-400 border border-gray-500/30">Disabled</Badge>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Switch checked={m.active !== false} onCheckedChange={() => toggleActive(m)} data-testid={`toggle-payment-method-${m.name}`} />
                  <Button variant="ghost" size="sm" onClick={() => remove(m)} className="text-red-400 hover:bg-red-500/10" data-testid={`delete-payment-method-${m.name}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
